import React from "react";
import { Logo, Chip, MatchRing, AvailBadge } from "./ui";

const S = "/screens";
const CANDIDATE = [
  ["01", "Splash / welcome", "Value proposition — get-started CTA anchored bottom.", `${S}/01_splash.jpg`],
  ["02", "Sign in", "Email + Google + Mobile OTP options.", `${S}/02_login.jpg`],
  ["03", "OTP verification", "6-digit code · resend timer · mocked with 123456.", `${S}/03_otp.jpg`],
  ["04", "Declare availability", "THE core moment — immediate or notice + end date.", `${S}/04_availability.jpg`],
  ["05", "Profile builder", "Live 0–100% completion score; ≥70% to apply.", `${S}/05_profile_build.jpg`],
  ["06", "Home feed", "Availability-aware match ranking; countdown card.", `${S}/06_home_feed.jpg`],
  ["07", "Search & filters", "Skills, location, joining timeline filter chips.", `${S}/07_job_search.jpg`],
  ["08", "Job detail", "Match breakdown + apply CTA + save.", `${S}/08_job_detail.jpg`],
  ["09", "Screening", "≤5 screening questions, resume auto-attached.", `${S}/09_apply.jpg`],
  ["10", "Submitted", "Confirmation + track application CTA.", `${S}/10_apply_success.jpg`],
  ["11", "Tracker", "6-stage pipeline visual per application.", `${S}/11_tracker.jpg`],
  ["12", "My profile", "Self-view, completion score, availability chip.", `${S}/12_profile.jpg`],
];

const EMPLOYER = [
  ["01", "Employer sign-in", "Marketing left · sign-in right · social proof.", `${S}/emp_01_login.jpg`],
  ["02", "Verify company", "GST/CIN + logo upload; goes to admin approval.", `${S}/emp_02_verify.jpg`],
  ["03", "Choose plan", "Free / Pro (14-day trial) / Enterprise.", `${S}/emp_03_plans.jpg`],
  ["04", "Razorpay checkout", "GST inclusive · UPI/Card/Netbanking/Wallets.", `${S}/emp_04_razorpay.jpg`],
  ["05", "Dashboard", "Stat tiles + 30-day funnel + urgent matches.", `${S}/emp_05_dashboard.jpg`],
  ["06", "Jobs", "Job posts table, max-joining chip on every row.", `${S}/emp_06_jobs.jpg`],
  ["07", "Candidate discovery", "Availability-first ordering with unlock credits.", `${S}/emp_07_candidates.jpg`],
  ["08", "Kanban board", "6-stage drag-drop pipeline · match ring on every card.", `${S}/emp_08_kanban.jpg`],
  ["09", "Analytics", "Applications by role + hire metrics.", `${S}/emp_09_analytics.jpg`],
  ["10", "Billing", "Plan status, credits, invoices.", `${S}/emp_10_billing.jpg`],
  ["11", "Post job modal", "'Maximum joining timeline' emphasized as critical field.", `${S}/emp_11_post_job.jpg`],
];

const ADMIN = [
  ["01", "Platform overview", "Stat grid + signup chart + availability distribution.", `${S}/adm_01_overview.jpg`],
  ["02", "Users", "Filters: candidate/employer/pending/suspended · suspend action.", `${S}/adm_02_users.jpg`],
  ["03", "Companies", "Verification approvals via GST/CIN.", `${S}/adm_03_companies.jpg`],
  ["04", "Job moderation", "Flag/remove inappropriate postings.", `${S}/adm_04_jobs.jpg`],
  ["05", "Payments", "MRR + successful/failed txn ledger.", `${S}/adm_05_payments.jpg`],
  ["06", "Support tickets", "Priority + ownership queue.", `${S}/adm_06_tickets.jpg`],
  ["07", "Config", "Skill library, cities, plan pricing.", `${S}/adm_07_config.jpg`],
];

export default function PrintDeck({ onExit }) {
  return (
    <div className="min-h-screen bg-white text-[var(--ink)]">
      <div className="no-print sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[var(--line)] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3"><Logo small/><span className="text-sm text-[var(--muted)]">PDF Export — {new Date().toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"})}</span></div>
        <div className="flex gap-2">
          <a href="/TalentXpres-Prototype-v1.pdf" download className="btn btn-primary text-sm">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12M6 9l6 6 6-6M4 21h16"/></svg>
            Download PDF
          </a>
          <button onClick={() => window.print()} className="btn btn-ghost text-sm">Print</button>
          <button onClick={onExit} className="btn btn-ghost text-sm">Back</button>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-8 py-10 space-y-16">
        {/* Cover */}
        <section className="text-center py-16 border-b border-[var(--line)]">
          <div className="inline-block"><Logo/></div>
          <div className="mt-8 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Product prototype · Confidential</div>
          <h1 className="font-display text-6xl mt-4 leading-[1.02]">TalentXpres<br/><span className="text-[var(--brand-2)]">v1 MVP</span></h1>
          <p className="mt-6 text-[var(--ink-2)] max-w-lg mx-auto">A two-sided urgent-hiring platform for the Indian metro market. Availability-first matching between employers with urgent openings and candidates on notice period or immediately available.</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg mx-auto text-left">
            <Stat label="Product surfaces" value="3"/>
            <Stat label="Screens" value="30"/>
            <Stat label="India metros" value="6"/>
          </div>
        </section>

        {/* Thesis */}
        <section className="break-before-page">
          <SectionLabel n="01" t="The product thesis"/>
          <h2 className="font-display text-4xl mt-4 leading-tight">Hire people who <span className="text-[var(--brand-2)]">can actually join</span>.</h2>
          <p className="mt-4 text-[var(--ink-2)] leading-relaxed">{`India's mid-market employers lose 40 days on candidates who accept an offer but can't join for 90+ days. TalentXpres removes that friction by making the joining timeline the primary filter — not a footnote at offer stage.`}</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {n:"01", t:"Availability first", d:"Candidates declare notice-end date."},
              {n:"02", t:"Weighted matching", d:"30/25/15/12/10/8 signal weighting."},
              {n:"03", t:"Manual verification", d:"Employers vetted via GST/CIN."},
              {n:"04", t:"Kanban-native", d:"Zero-drop application pipeline."},
            ].map(x => (
              <div key={x.n} className="border-t border-[var(--line)] pt-3">
                <div className="font-mono text-xs text-[var(--muted)]">{x.n}</div>
                <div className="font-display text-lg mt-1">{x.t}</div>
                <p className="text-xs text-[var(--ink-2)] mt-1">{x.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Design system */}
        <section className="break-before-page">
          <SectionLabel n="02" t="Design system snapshot"/>
          <div className="mt-6 grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-[var(--muted)] mb-2">Color</div>
              <div className="space-y-2">
                {[
                  {n:"Deep forest — Brand", c:"#0F3D2E", t:"white"},
                  {n:"Emerald — Availability", c:"#14B87A", t:"#05261B"},
                  {n:"Coral — Urgency accent", c:"#FF6A3D", t:"white"},
                  {n:"Paper — Background", c:"#FAF8F3", t:"#0B0F14"},
                  {n:"Ink — Foreground", c:"#0B0F14", t:"#FAF8F3"},
                ].map(s => (
                  <div key={s.n} style={{background:s.c, color:s.t}} className="rounded-lg px-4 py-3 text-xs flex justify-between font-mono">
                    <span>{s.n}</span><span>{s.c}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--muted)] mb-2">Typography</div>
              <div className="space-y-3">
                <div><div className="text-xs text-[var(--muted)]">Display · Bricolage Grotesque</div><div className="font-display text-4xl leading-none">Hire faster.</div></div>
                <div><div className="text-xs text-[var(--muted)]">Body · Manrope</div><div className="text-sm">The rest of the interface uses a clean humanist grotesque with high x-height for scan-ability.</div></div>
                <div><div className="text-xs text-[var(--muted)]">Mono · JetBrains Mono</div><div className="font-mono text-xs">avail=35d · match=94% · ₹28L</div></div>
              </div>
              <div className="mt-6 text-xs text-[var(--muted)] mb-2">Availability badges</div>
              <div className="flex gap-2 flex-wrap">
                <AvailBadge status="immediate" days={0}/>
                <AvailBadge status="notice" days={12}/>
                <AvailBadge status="notice" days={35}/>
                <AvailBadge status="notice" days={72}/>
              </div>
              <div className="mt-4 text-xs text-[var(--muted)] mb-2">Match rings</div>
              <div className="flex gap-3"><MatchRing score={94}/><MatchRing score={72}/><MatchRing score={48}/></div>
            </div>
          </div>
        </section>

        {/* Candidate — REAL SCREENSHOTS */}
        <section className="break-before-page">
          <SectionLabel n="03" t="Candidate mobile app · 12 screens"/>
          <p className="text-[var(--ink-2)] mt-3">React Native · Bottom-tab · Onboarding → Feed → Apply → Track.</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-6">
            {CANDIDATE.map(([n, title, sub, src]) => (
              <ScreenCard key={n} n={n} title={title} sub={sub} src={src} aspect="9/16"/>
            ))}
          </div>
        </section>

        {/* Employer — REAL SCREENSHOTS */}
        <section className="break-before-page">
          <SectionLabel n="04" t="Employer web console · 11 screens"/>
          <p className="text-[var(--ink-2)] mt-3">React web · Sidebar nav · Dashboard-first with Kanban centerpiece.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {EMPLOYER.map(([n, title, sub, src]) => (
              <ScreenCard key={n} n={n} title={title} sub={sub} src={src} aspect="16/10"/>
            ))}
          </div>
        </section>

        {/* Admin — REAL SCREENSHOTS */}
        <section className="break-before-page">
          <SectionLabel n="05" t="Super Admin console · 7 sections"/>
          <p className="text-[var(--ink-2)] mt-3">Internal utility-first. Verifications, moderation, revenue monitoring.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {ADMIN.map(([n, title, sub, src]) => (
              <ScreenCard key={n} n={n} title={title} sub={sub} src={src} aspect="16/10"/>
            ))}
          </div>
        </section>

        {/* Matching */}
        <section className="break-before-page">
          <SectionLabel n="06" t="Matching engine — v1"/>
          <p className="text-[var(--ink-2)] mt-3">Weighted score per candidate–job pair. Ranked feed for candidates; ranked applications for employers.</p>
          <div className="mt-6 card p-6">
            {[
              {l:"Availability alignment", w:30, c:"var(--brand-2)"},
              {l:"Skills match", w:25, c:"#5FCE9A"},
              {l:"Role/title match", w:15, c:"#F5B849"},
              {l:"Experience match", w:12, c:"#FF6A3D"},
              {l:"Location match", w:10, c:"#8B7CFF"},
              {l:"Salary alignment", w:8, c:"#B7F0C5"},
            ].map(r => (
              <div key={r.l} className="flex items-center gap-4 py-2">
                <div className="w-56 text-sm">{r.l}</div>
                <div className="flex-1 h-6 rounded bg-[var(--paper-2)] overflow-hidden"><div className="h-full" style={{width:`${r.w*3}%`,background:r.c}}/></div>
                <div className="w-10 text-sm font-semibold text-right">{r.w}%</div>
              </div>
            ))}
            <div className="mt-4 text-xs text-[var(--ink-2)]">≥70 = Top match · 50–70 = Good match · &lt;50 = Other</div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="break-before-page">
          <SectionLabel n="07" t="Delivery timeline · 20 weeks"/>
          <div className="mt-6 space-y-3">
            {[
              {p:"Phase 0", t:"Design sign-off", w:"Weeks 1–4", d:"This deck, Figma system, interactive prototype", done:true},
              {p:"Phase 1", t:"Foundation", w:"Weeks 5–7", d:"Repo, CI/CD, Postgres, Node backend, auth"},
              {p:"Phase 2", t:"Candidate app", w:"Weeks 8–12", d:"React Native · onboarding → apply → track"},
              {p:"Phase 3", t:"Employer web", w:"Weeks 11–15", d:"Verification, Razorpay, Kanban, discovery"},
              {p:"Phase 4", t:"Matching + Admin", w:"Weeks 15–17", d:"Engine tuning + super admin console"},
              {p:"Phase 5", t:"QA + Launch", w:"Weeks 18–20", d:"Beta 5 employers × 100 candidates, stores"},
            ].map(r => (
              <div key={r.p} className="card p-4 flex items-start gap-4">
                <div className="w-16 font-mono text-xs text-[var(--muted)]">{r.p}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg">{r.t}</div>
                    {r.done && <Chip tone="mint">Delivered</Chip>}
                  </div>
                  <div className="text-xs text-[var(--muted)]">{r.w}</div>
                  <div className="text-sm text-[var(--ink-2)] mt-1">{r.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 card p-5 flex items-center justify-between bg-[var(--brand-3)]/30 border-[var(--brand-2)]/40">
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Total build timeline</div>
              <div className="font-display text-3xl mt-1">20 weeks</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-[var(--brand)]">First live user</div>
              <div className="font-display text-3xl mt-1">Week 18</div>
            </div>
          </div>
        </section>

        {/* Team & Resources */}
        <section className="break-before-page">
          <SectionLabel n="08" t="Team & resources"/>
          <p className="text-[var(--ink-2)] mt-3">Recommended in-house or single-agency team. Agency pricing typically 1.5–2× in-house.</p>
          <div className="mt-6 card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
                <tr><th className="px-5 py-3">Role</th><th className="px-5 py-3">Count</th><th className="px-5 py-3">Engagement</th><th className="px-5 py-3">Duration</th></tr>
              </thead>
              <tbody>
                {[
                  {r:"Product Manager", c:"1", e:"Full-time", d:"20 weeks"},
                  {r:"UI/UX Designer (Figma)", c:"1", e:"Full-time · Weeks 1–6, then part-time", d:"20 weeks"},
                  {r:"Frontend Engineer (React web)", c:"1", e:"Full-time", d:"Weeks 5–20 (16 weeks)"},
                  {r:"Mobile Engineer (React Native)", c:"1", e:"Full-time", d:"Weeks 5–20 (16 weeks)"},
                  {r:"Backend Engineer (Node.js + Postgres)", c:"2", e:"Full-time", d:"Weeks 5–20 (16 weeks)"},
                  {r:"QA Engineer", c:"1", e:"Full-time", d:"Weeks 12–20 (9 weeks)"},
                  {r:"DevOps", c:"0.5", e:"Part-time", d:"Weeks 5–20 (16 weeks)"},
                ].map(row => (
                  <tr key={row.r} className="border-t border-[var(--line)]">
                    <td className="px-5 py-3 font-medium">{row.r}</td>
                    <td className="px-5 py-3">{row.c}</td>
                    <td className="px-5 py-3 text-[var(--ink-2)]">{row.e}</td>
                    <td className="px-5 py-3 text-[var(--muted)]">{row.d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-xs text-[var(--muted)]">Total headcount: 7 people (6.5 FTE across the 20 weeks)</div>
        </section>

        {/* Budget */}
        <section className="break-before-page">
          <SectionLabel n="09" t="Budget · MVP v1 build"/>
          <p className="text-[var(--ink-2)] mt-3">Indicative one-time build cost at India rates (INR). Range reflects seniority and agency vs in-house mix.</p>

          <div className="mt-6 card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
                <tr><th className="px-5 py-3">Line item</th><th className="px-5 py-3 text-right">Low</th><th className="px-5 py-3 text-right">High</th></tr>
              </thead>
              <tbody>
                {[
                  {l:"Design (Figma) — 4–6 weeks", low:"₹3,00,000", high:"₹4,50,000"},
                  {l:"Backend development (Node + Postgres)", low:"₹10,00,000", high:"₹14,00,000"},
                  {l:"Web frontend (employer + admin)", low:"₹6,00,000", high:"₹8,00,000"},
                  {l:"Mobile app (candidate · React Native)", low:"₹7,00,000", high:"₹9,00,000"},
                  {l:"QA + UAT", low:"₹1,50,000", high:"₹2,50,000"},
                  {l:"DevOps + infra setup", low:"₹1,00,000", high:"₹1,50,000"},
                  {l:"PM + project overhead", low:"₹2,50,000", high:"₹3,50,000"},
                ].map(row => (
                  <tr key={row.l} className="border-t border-[var(--line)]">
                    <td className="px-5 py-3">{row.l}</td>
                    <td className="px-5 py-3 text-right font-mono">{row.low}</td>
                    <td className="px-5 py-3 text-right font-mono">{row.high}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--ink)] bg-[var(--paper-2)]">
                  <td className="px-5 py-4 font-display text-lg">Total prototype (MVP v1)</td>
                  <td className="px-5 py-4 text-right font-display text-lg">₹31,00,000</td>
                  <td className="px-5 py-4 text-right font-display text-lg">₹43,00,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="card p-5">
              <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Lower bound</div>
              <div className="font-display text-4xl mt-1">₹31L</div>
              <div className="text-xs text-[var(--ink-2)] mt-1">In-house, mid-level team</div>
            </div>
            <div className="card p-5 bg-[var(--brand-3)]/30 border-[var(--brand-2)]/40">
              <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Recommended budget</div>
              <div className="font-display text-4xl mt-1">₹37L</div>
              <div className="text-xs text-[var(--ink-2)] mt-1">Blended · realistic mid-point</div>
            </div>
            <div className="card p-5">
              <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Upper bound</div>
              <div className="font-display text-4xl mt-1">₹43L</div>
              <div className="text-xs text-[var(--ink-2)] mt-1">Agency, senior team</div>
            </div>
          </div>
        </section>

        {/* Recurring costs */}
        <section className="break-before-page">
          <SectionLabel n="10" t="Recurring monthly costs · post-launch"/>
          <p className="text-[var(--ink-2)] mt-3">Infrastructure + third-party services once TalentXpres is live at small scale (early adopters).</p>

          <div className="mt-6 card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
                <tr><th className="px-5 py-3">Item</th><th className="px-5 py-3 text-right">Monthly cost (INR)</th></tr>
              </thead>
              <tbody>
                {[
                  {l:"Cloud hosting (containers, small scale)", v:"₹15,000 – ₹30,000"},
                  {l:"MongoDB Atlas / Postgres managed", v:"₹8,000 – ₹15,000"},
                  {l:"SendGrid (transactional email)", v:"₹2,000 – ₹5,000"},
                  {l:"Object storage + CDN (resumes, logos)", v:"₹3,000 – ₹8,000"},
                  {l:"Razorpay", v:"~2% per transaction"},
                  {l:"FCM push notifications", v:"Free tier"},
                ].map(r => (
                  <tr key={r.l} className="border-t border-[var(--line)]">
                    <td className="px-5 py-3">{r.l}</td>
                    <td className="px-5 py-3 text-right font-mono">{r.v}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--ink)] bg-[var(--paper-2)]">
                  <td className="px-5 py-4 font-display text-lg">Total monthly infra</td>
                  <td className="px-5 py-4 text-right font-display text-lg">₹28,000 – ₹60,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 card p-5">
            <div className="text-xs uppercase tracking-widest text-[var(--muted)] mb-2">Assumptions</div>
            <ul className="text-sm text-[var(--ink-2)] space-y-1.5 list-disc pl-5">
              <li>Costs vary based on team seniority, in-house vs agency, and shared roles</li>
              <li>Agency pricing typically 1.5–2× the above ranges</li>
              <li>v1 targets India only, English only, INR pricing</li>
              <li>Notice-period verification is self-declared in v1 (OCR added in Phase 2)</li>
              <li>Legal/compliance (T&Cs, privacy policy, GST) handled outside dev scope</li>
              <li>Play Store + App Store developer accounts assumed already available</li>
            </ul>
          </div>
        </section>

        <footer className="border-t border-[var(--line)] pt-6 pb-12 flex justify-between text-xs text-[var(--muted)]">
          <div>© 2026 TalentXpres · Prototype v1</div>
          <div>Mumbai · Bangalore · Confidential</div>
        </footer>
      </div>
    </div>
  );
}

function ScreenCard({ n, title, sub, src, aspect }) {
  return (
    <div className="card p-3 break-inside-avoid">
      <div className="rounded-lg bg-[var(--paper-2)] overflow-hidden border border-[var(--line)]" style={{aspectRatio: aspect}}>
        <img src={src} alt={title} className="w-full h-full object-cover object-top" loading="lazy"/>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-mono text-xs text-[var(--muted)]">{n}</div>
        <div className="font-display text-base">{title}</div>
      </div>
      <div className="text-xs text-[var(--ink-2)] mt-1 leading-snug">{sub}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[var(--line)] rounded-lg p-3">
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function SectionLabel({ n, t }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="font-mono text-xs text-[var(--muted)]">{n}</div>
      <div className="h-px flex-1 bg-[var(--line)]"/>
      <div className="font-display text-xl">{t}</div>
    </div>
  );
}
